/**
 * What the lead card should say about Salesforce, from two sources that
 * disagree about how much they know.
 *
 * `Lead` carries a copy of the Salesforce fields taken at the last sync;
 * `/api/leads/{id}/salesforce` is the live read and is the only thing that
 * knows whether an owner is a person or a shared queue. The live read is also
 * the one that can be slow, unconfigured, or 404 — so the card renders the
 * synced copy immediately and upgrades it in place, rather than showing
 * nothing until a request that may never succeed comes back.
 */

import type { LeadSalesforce } from '@/shared/api/leads'
import type { Lead, SfAccount } from '@/shared/types'

export type SfOwnerKind = 'user' | 'queue' | null

export interface SalesforceView {
  /**
   * True when the company is known to exist in Salesforce (a Lead, an Account
   * or an open Opportunity), false when the live read found none of them,
   * null while nothing has confirmed either way.
   * Distinguishing "not in Salesforce" from "we have not looked" matters: the
   * first is information a BDR can act on, the second is not.
   */
  present: boolean | null
  status: string | null
  owner: string | null
  /** Null means the owner's type is genuinely unknown, never "assume person". */
  ownerKind: SfOwnerKind
  rating: string | null
  lastActivityAt: string | null
  convertedAt: string | null
  openOpportunityStage: string | null
  account: SfAccount | null
  link: string | null
  /** True once the live read has answered, so the card can stop hedging. */
  live: boolean
  uncheckedReason: string | null
}

const clean = (v: string | null | undefined): string | null => {
  const s = (v ?? '').trim()
  return s ? s : null
}

/** The synced copy on the Lead row, on its own. */
function fromLead(lead: Lead): Omit<SalesforceView, 'live'> {
  const status = clean(lead.sfStatus)
  const owner = clean(lead.sfLeadOwner)
  const link = clean(lead.sfLink)
  const lastActivityAt = clean(lead.sfLastActivityAt)
  const openOpportunityStage = clean(lead.sfOpenOpportunityStage)
  const account = lead.sfAccount ?? null
  const known = !!(status || owner || link || lastActivityAt || openOpportunityStage || account)
  return {
    present: known ? true : null,
    status,
    owner,
    ownerKind: null,
    rating: null,
    lastActivityAt,
    convertedAt: null,
    openOpportunityStage,
    account,
    link,
    uncheckedReason: null,
  }
}

/**
 * Merge the live read over the synced copy.
 *
 * A live `found: false` wins outright for the Lead's fields — it is the newer,
 * more specific answer, and continuing to show a stale status for a lead that
 * has since been deleted in Salesforce is worse than showing nothing.
 * Otherwise each field falls back to the synced value, because the live
 * endpoint may know the lead without knowing every field about it.
 *
 * The live Account wins the same way, unless its read failed. The live read
 * does not look for Opportunities, so the synced one stands. "Inte i
 * Salesforce" (`present: false`) needs no Lead, no Account and no Opportunity.
 *
 * An unconfigured Salesforce (`configured: false`) has not actually looked, so
 * it is treated like no live answer at all rather than like `found: false` —
 * "not connected" must never read as the stronger claim "not in Salesforce".
 */
export function resolveSalesforceView(
  lead: Lead, live: LeadSalesforce | undefined,
): SalesforceView {
  const base = fromLead(lead)
  if (!live || !live.configured) return { ...base, live: false }
  if (live.checked === false) {
    return { ...base, live: false, uncheckedReason: "Salesforce har inte kontrollerats eftersom organisationsnummer saknas" }
  }
  const account = live.accountsError ? base.account : liveAccount(live.account)
  if (!live.found) {
    return {
      present: !!(account || base.openOpportunityStage),
      status: null, owner: null, ownerKind: null, rating: null,
      lastActivityAt: null, convertedAt: null, link: null,
      openOpportunityStage: base.openOpportunityStage,
      account,
      live: true,
      uncheckedReason: null,
    }
  }
  return {
    present: true,
    status: clean(live.status) ?? base.status,
    owner: clean(live.owner) ?? base.owner,
    ownerKind: live.ownerKind ?? null,
    rating: clean(live.rating),
    lastActivityAt: clean(live.lastActivityAt) ?? base.lastActivityAt,
    convertedAt: clean(live.convertedAt),
    openOpportunityStage: clean(live.openOpportunityStage) ?? base.openOpportunityStage,
    account,
    link: clean(live.link) ?? base.link,
    live: true,
    uncheckedReason: null,
  }
}

function liveAccount(a: LeadSalesforce['account']): SfAccount | null {
  if (!a) return null
  return {
    type: a.type, owner: a.owner, parentName: a.parentName, parentType: a.parentType,
    customer: a.customer, parentCustomer: a.parentCustomer,
  }
}

/** The Account badge: an existing customer, or a company Salesforce knows
 *  as some other kind of Account. `person` masks the owner's name. */
export function accountBadgeText(
  a: SfAccount, person: (value?: string | null) => string = v => v ?? '',
): string {
  const label = a.customer ? 'Befintlig kund i Salesforce' : 'Finns i Salesforce'
  const owner = clean(a.owner)
  return owner ? `${label} · ${person(owner)}` : label
}

/** The group line for a subsidiary, or null without a parent. Shown only:
 *  a customer parent never takes the subsidiary out of the queues. */
export function parentBadgeText(a: SfAccount): string | null {
  const parent = clean(a.parentName)
  if (!parent) return null
  return a.parentCustomer ? `Del av ${parent} · koncernen är kund` : `Del av ${parent}`
}

/** Whether there is anything to put on screen. A card for a lead nobody has
 *  ever pushed to Salesforce should not grow an empty Salesforce strip. */
export function hasSalesforceFacts(v: SalesforceView): boolean {
  if (v.uncheckedReason) return true
  if (v.present === false) return true
  return !!(v.status || v.owner || v.link || v.lastActivityAt || v.convertedAt || v.openOpportunityStage || v.account)
}

/** What to call the owner field. A queue is not a colleague, and an unknown
 *  kind must not be dressed up as either. */
export function ownerFieldLabel(kind: SfOwnerKind): string {
  if (kind === 'queue') return 'Kö'
  if (kind === 'user') return 'Ägare'
  return 'SF-ägare'
}

export function ownerFieldTitle(kind: SfOwnerKind, owner: string): string {
  if (kind === 'queue') return `${owner} är en Salesforce-kö, inte en person`
  if (kind === 'user') return `Ägs i Salesforce av ${owner}`
  return `Salesforce-ägare: ${owner}. Om det är en person eller en delad kö är ännu okänt.`
}
